/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Component,
  Vue,
} from 'vue-property-decorator';

import api from '@/api';
import { formatAddress } from '@/lib/address';
import { assertExists } from '@/lib/assert';
import {
  Money,
  formatMoney,
} from '@/lib/money';
import {
  Address,
  DraftOrder,
  StoreAddress,
  DraftOrderData,
} from '@/models';

const CheckoutViewProps = Vue.extend({
  props: {
    orderId: {
      type: String,
      required: true,
    },
  },
});

@Component
export default class CheckoutView extends CheckoutViewProps {
  public order: DraftOrder | null = null;

  public created() {
    api.fetchDraftOrder(this.orderId)
      .then((orderData) => this.loadOrder(orderData));
  }

  public formatAddress(address: Address) {
    return formatAddress(address);
  }

  public formatMoney(price: Money) {
    return formatMoney(price);
  }

  public async setItemQuantity(itemId: string, quantity: any) {
    const order = assertExists(this.order);
    return api.setDraftOrderItemQuantity(order, itemId, quantity)
      .then((orderData) => this.loadOrder(orderData));
  }

  public async setFulfillment(index: number, fulfillment: { selected: string, selectedAddress?: Address }) {
    const order = assertExists(this.order);

    const newFulfillments = order!.fulfillments.map((curFulfillment, fulfillmentIndex) => ({
      serviceId: (fulfillmentIndex === index) ? fulfillment.selected : curFulfillment.service.id,
      addressId:  (fulfillmentIndex === index) ?
        ((fulfillment.selectedAddress) ? fulfillment.selectedAddress.id : undefined) :
        ((curFulfillment.shippingAddress) ? curFulfillment.shippingAddress.id : undefined),
      itemIds: curFulfillment.items.map((item) => (item.id)),
    }));

    return api.setDraftOrderFulfillments(order, newFulfillments)
      .then((orderData) => this.loadOrder(orderData));
  }

  private loadOrder(orderData: DraftOrderData) {
    this.order = new DraftOrder(orderData);
  }
}
